import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format, startOfWeek, addWeeks } from "date-fns";
import { fr } from "date-fns/locale";
import { Lock, LogOut, ChevronLeft, ChevronRight, Loader2, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sa_admin_token";
const DAY_NAMES = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-0)] px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-md border border-white/10 bg-[var(--bg-1)] p-8">
        <img src={LOGO_URL} alt="Speed Auto Lavage" className="h-16 w-auto object-contain mix-blend-screen mb-6" />
        <div className="flex items-center gap-2 mb-6">
          <Lock size={16} className="text-[var(--blue)]" />
          <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-white">Espace propriétaire</span>
        </div>
        <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Email</label>
        <input
          data-testid="admin-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 mb-4 w-full h-12 px-3 rounded-sm bg-transparent border border-white/15 text-white focus:outline-none focus:border-[var(--blue)]"
          placeholder="admin@speedauto.fr"
        />
        <label className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Mot de passe</label>
        <input
          data-testid="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 mb-6 w-full h-12 px-3 rounded-sm bg-transparent border border-white/15 text-white focus:outline-none focus:border-[var(--blue)]"
          placeholder="••••••••"
        />
        <button
          data-testid="admin-login-submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 bg-white text-black font-mono2 text-xs font-bold uppercase tracking-[0.2em] px-6 py-4 rounded-sm hover:bg-[var(--blue)] hover:text-white transition-colors duration-300 disabled:opacity-60"
        >
          {loading ? <Loader2 size={15} className="animate-spin" /> : null} Se connecter
        </button>
      </form>
    </div>
  );
};

const Planning = ({ token, onLogout }) => {
  const [monday, setMonday] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async (mon) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/week`, {
        params: { start: format(mon, "yyyy-MM-dd") },
        headers: { Authorization: `Bearer ${token}` },
      });
      setWeek(data);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    load(monday);
  }, [monday, load]);

  const toggle = async (day, slot) => {
    if (slot.status === "booked" || slot.status === "past") return;
    const endpoint = slot.status === "blocked" ? "unblock" : "block";
    setBusy(`${day.date}-${slot.time}`);
    try {
      await axios.post(`${API}/admin/${endpoint}`, { date: day.date, time: slot.time }, auth);
      await load(monday);
    } catch {
      toast.error("Action impossible");
    } finally {
      setBusy("");
    }
  };

  const times = week?.days?.[0]?.slots?.map((s) => s.time) || [];

  return (
    <div className="min-h-screen bg-[var(--bg-0)] text-white">
      <div className="noise-overlay" />
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-[70px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="logo" className="h-10 w-auto object-contain mix-blend-screen" />
            <span className="hidden sm:inline font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-2)]">Planning</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="font-mono2 text-[11px] uppercase tracking-[0.18em] text-[var(--txt-1)] hover:text-white transition-colors">Voir le site</a>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 rounded-sm font-mono2 text-[11px] uppercase tracking-[0.15em] hover:border-white/40 transition-colors">
              <LogOut size={13} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-bold uppercase tracking-tighter text-3xl sm:text-4xl">Planning</h1>
            <p className="mt-1 font-mono2 text-[11px] uppercase tracking-[0.15em] text-[var(--txt-2)] flex items-center gap-2">
              <CalIcon size={13} className="text-[var(--blue)]" />
              Semaine du {format(monday, "dd MMM", { locale: fr })} au {format(addWeeks(monday, 0), "dd", { locale: fr })}
              {week && ` · ${format(new Date(week.days[5].date), "dd MMM yyyy", { locale: fr })}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="week-prev" onClick={() => setMonday(addWeeks(monday, -1))} className="h-10 w-10 flex items-center justify-center border border-white/15 rounded-sm hover:border-white/40 transition-colors"><ChevronLeft size={18} /></button>
            <button onClick={() => setMonday(startOfWeek(new Date(), { weekStartsOn: 1 }))} className="px-4 h-10 border border-white/15 rounded-sm font-mono2 text-[11px] uppercase tracking-[0.15em] hover:border-white/40 transition-colors">Aujourd'hui</button>
            <button data-testid="week-next" onClick={() => setMonday(addWeeks(monday, 1))} className="h-10 w-10 flex items-center justify-center border border-white/15 rounded-sm hover:border-white/40 transition-colors"><ChevronRight size={18} /></button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-5 font-mono2 text-[10px] uppercase tracking-[0.15em] text-[var(--txt-2)]">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border border-white/20" /> Libre</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[var(--blue)]" /> Réservé</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-white/15" /> Bloqué</span>
          <span className="text-[var(--txt-2)]/60">Cliquez une case libre pour la bloquer</span>
        </div>

        {loading || !week ? (
          <div className="flex items-center gap-2 text-[var(--txt-2)] py-20 justify-center"><Loader2 className="animate-spin" size={18} /> Chargement…</div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-white/10">
            <table className="w-full border-collapse min-w-[720px]" data-testid="planning-table">
              <thead>
                <tr>
                  <th className="bg-[var(--bg-2)] p-3 text-left font-mono2 text-[10px] uppercase tracking-[0.2em] text-[var(--txt-2)] w-20">Heure</th>
                  {week.days.map((d, i) => (
                    <th key={d.date} className="bg-[var(--bg-2)] p-3 border-l border-white/10 text-center">
                      <div className="font-display font-semibold text-sm">{DAY_NAMES[i]}</div>
                      <div className="font-mono2 text-[10px] text-[var(--txt-2)]">{format(new Date(d.date), "dd/MM")}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {times.map((t, ri) => (
                  <tr key={t}>
                    <td className="bg-[var(--bg-1)] p-3 font-mono2 text-sm font-bold border-t border-white/10">{t}</td>
                    {week.days.map((d) => {
                      const slot = d.slots[ri];
                      const key = `${d.date}-${slot.time}`;
                      const isBusy = busy === key;
                      let cls = "border-white/15 hover:border-[var(--blue)] cursor-pointer bg-transparent";
                      if (slot.status === "booked") cls = "bg-[var(--blue)] border-[var(--blue)] text-white cursor-default";
                      else if (slot.status === "blocked") cls = "bg-white/15 border-white/10 cursor-pointer";
                      else if (slot.status === "past") cls = "bg-white/[0.02] border-white/5 text-[var(--txt-2)]/40 cursor-not-allowed";
                      return (
                        <td key={d.date} className="border-t border-l border-white/10 p-1">
                          <button
                            data-testid={`cell-${d.date}-${slot.time}`}
                            onClick={() => toggle(d, slot)}
                            disabled={slot.status === "past" || slot.status === "booked" || isBusy}
                            title={slot.reservation ? `${slot.reservation.name} · ${slot.reservation.formule} · ${slot.reservation.vehicle_label} · ${slot.reservation.price}€ · ${slot.reservation.phone}` : ""}
                            className={`w-full min-h-[54px] rounded-sm border text-center px-1 py-1 transition-colors duration-200 ${cls}`}
                          >
                            {slot.status === "booked" ? (
                              <span className="block leading-tight">
                                <span className="font-mono2 text-[11px] font-bold block truncate">{slot.reservation?.name}</span>
                                <span className="font-mono2 text-[9px] uppercase opacity-80 block truncate">{slot.reservation?.formule} · {slot.reservation?.price}€</span>
                              </span>
                            ) : slot.status === "blocked" ? (
                              <span className="font-mono2 text-[9px] uppercase tracking-wide text-[var(--txt-1)]">Bloqué</span>
                            ) : slot.status === "past" ? (
                              <span className="font-mono2 text-[9px] uppercase">—</span>
                            ) : (
                              <span className="font-mono2 text-[9px] uppercase text-[var(--txt-2)]">{isBusy ? "…" : "Libre"}</span>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };
  if (!token) return <Login onLogin={setToken} />;
  return <Planning token={token} onLogout={logout} />;
}
